import {delay, guid} from "./commonUtility";
import {downloadFull} from "./awsUtility";
import isEmpty from "lodash/isEmpty";
import isNumber from "lodash/isNumber";
import {actions, category, events as event, TrackActionType} from '../constants/enums';
import {copyBuffer, createOfflineAudioContext, getAudioContext, isMidi, parseMidiBuffer} from "./audioUtility";
/* eslint-disable */
import WebAudioFontPlayer from 'webaudiofont';
import {SimpleFilter, SoundTouch} from "../utils/st";
import {customEvent} from "./analyticsUtil";
/* eslint-enabled */


const emptyFunction = () => {
};

const bufferSize = 8192;

class AudioManager {

    constructor() {
        this.playerMap = new Map(); // playerId -> player
        this.rawBufferMap = []; // fileId -> rawBuffer (temp)
        this.bufferMap = []; // fileId -> playBuffer (decoded)
        this.unsubscribeMap = []; // fileId -> playBuffer (decoded)
        this.bufferPromiseMap = new Map(); // fileId -> bufferPromise
        this.fileIdToPlayerMap = new Map(); // fileId -> Players
        this.clipboardInfo = null; // temporary clipboard buffer
        this.scriptNode = null;
        // setInterval(() => {
        //     let total = 0;
        //     for (let key in this.bufferMap) {
        //         if (key && this.bufferMap[key] && this.bufferMap[key].length) {
        //             total += this.bufferMap[key].length;
        //         }
        //     }
        //     console.log('Total: ' + total);
        // }, 1000);
    }


    initSplits = (track) => {
        track.splits = [{
            when: 0,
            trackOffset: 0,
            duration: track.originalDuration / track.playbackRate,
            fullDuration: track.originalDuration / track.playbackRate,
            start: 0,
            end: track.originalDuration / track.playbackRate,
            offset: 0,
            leftTrim: 0,
            rightTrim: 0,
            prevLeftTrim: 0,
            prevRightTrim: null
        }];
    };

    addChunkToBuffer = async (fileData, rawBuffer, player, duration = null) => {
        let fileId = fileData.fileId;
        if (this.unsubscribeMap[fileId]) {
            this.unsubscribeMap[fileId] = null;
            return null;
        }
        this.rawBufferMap[fileId] = rawBuffer;
        let buffer;
        if (isMidi(fileData.fileType)) {
            buffer = await this.decodeMidiBuffer(rawBuffer);
        } else {
            try {
                buffer = await this.decodeRawBuffer(rawBuffer);
            } catch (err) {
                console.log('failed to decode audio: ', err);
                return null;
            }
        }
        this.bufferMap[fileId] = buffer;
        player.duration = duration || buffer.duration;
        player.originalDuration = player.originalDuration || player.duration;
        player.paddingDuration = player.duration;
        return buffer;
    };

    /**
     * Subscribe the file to the audio store
     * @param fileData  {String}    The url to play
     * @param events {Object} onLoaded, onTimeUpdate, onPlay, onStopPause, onEnd, onDurationChange
     * @param runInLoop    {Boolean}   Indicator whether or not we are in loop mode
     * @param duration
     * @param useMediaElement
     * @returns {string}    The player id for further tracking
     */
    subscribe = async (fileData, events = {}, runInLoop = false, duration = null, useMediaElement = false) => {
        if (typeof fileData === 'string') {
            fileData = {
                fileId: fileData
            }
        }
        let id = guid();
        if (duration === 0) {
            duration = null;
        }
        let eventsWithDefault = {
            onLoaded: events.onLoaded || emptyFunction,
            onDurationChange: events.onDurationChange || emptyFunction,
            onTimeUpdate: events.onTimeUpdate || emptyFunction,
            onPlay: events.onPlay || emptyFunction,
            onStopPause: events.onStopPause || emptyFunction,
            onEnd: events.onEnd || emptyFunction,
            onUpdateProgress: events.onUpdateProgress || emptyFunction
        };
        let playerList = this.fileIdToPlayerMap.get(fileData.fileId);
        if (isEmpty(playerList)) {
            playerList = [];
            this.fileIdToPlayerMap.set(fileData.fileId, playerList);
        }
        playerList.push(id);

        const playbackRate = fileData.bpm && fileData.originalBPM ? fileData.bpm / fileData.originalBPM : 1;
        const pitch = !fileData.ignorePitch && fileData.pitch ? 1 + (Math.pow(2, 1 / 12) - 1) * fileData.pitch : 1;

        let player = {
            id: id,
            fileId: fileData.fileId,
            fileType: fileData.fileType,
            startOffset: 0,
            currentTime: 0,
            duration: duration,
            originalDuration: duration * fileData.speed,
            paddingDuration: duration,
            volume: 0.5,
            masterVolume: 0.5,
            playbackRate: playbackRate,
            playbackRateFromLast: 1,
            pitch: pitch,
            ignorePitch: fileData.ignorePitch,
            loop: runInLoop,
            actions: new Set(),
            events: eventsWithDefault
        };
        this.playerMap.set(id, player);
        let bufferPromise = this.bufferPromiseMap.get(fileData.fileId);
        const isMidiFile = isMidi(fileData.fileType);
        if (isEmpty(bufferPromise)) {
            if (useMediaElement && !isMidiFile) {
                let url = '/audio/' + fileData.fileId;
                player.mediaElement = new Audio(url);
                player.mediaElement.oncanplay = () => {
                    eventsWithDefault.onLoaded(player);
                };

                player.duration = duration || player.mediaElement.duration;
                player.paddingDuration = player.duration;
                bufferPromise = Promise.resolve();
            } else {
                bufferPromise = new Promise(async (resolve) => {
                    let rawBuffer = this.rawBufferMap[fileData.fileId];
                    if (isEmpty(rawBuffer)) {
                        if (fileData.buffer) {
                            this.bufferMap[fileData.fileId] = fileData.buffer;
                            player.duration = duration || fileData.buffer.duration;
                            player.originalDuration = player.duration;
                            player.paddingDuration = player.duration;
                        } else {
                            let rawBuffer;
                            let buffer;
                            let numTrys = 0;
                            // let fileName = isMidiFile ? fileData.fileId : getFileIdWithBpm(fileData);
                            // if ((fileData.bpm !== fileData.originalBPM || (fileData.pitch !== 0)) && !isMidiFile && !fileData.isCopy) {
                            //     await updateAudioFileBPMPitch(fileData.fileId, fileData.originalBPM, fileData.bpm, fileData.pitch); // for now we ignore to response (which is the new bpm)
                            // }
                            while (!buffer && numTrys < 3) {
                                let fileId = fileData.fileId;
                                if (!isMidi(fileData.fileType) && fileData.useMonoVersion && numTrys === 0) {
                                    console.log('trying to use mono version for fileId: ' + fileId);
                                    fileId += '_mono';
                                }
                                rawBuffer = await downloadFull(fileId, (e) => {
                                    let pct;
                                    if (e && e.total === 0 && e.loaded > 0) {
                                        pct = 0;
                                    } else {
                                        pct = e ? (e.loaded / e.total) : 1;
                                    }
                                    eventsWithDefault.onUpdateProgress(pct);
                                });
                                if (!rawBuffer) {
                                    await delay(2000);
                                    numTrys++;
                                    continue;
                                }

                                buffer = await this.addChunkToBuffer(fileData, rawBuffer, player, duration);
                                if (buffer === null) {
                                    console.warn('Failed to read file: ', fileData.fileId, ' trying again');
                                    numTrys++;
                                }
                            }
                            if (!rawBuffer || !buffer) {
                                console.error('Failed to read file: ', fileData.fileId);
                                resolve(null);
                                return;
                            }
                        }
                    }
                    resolve();
                });
            }
            this.bufferPromiseMap.set(fileData.fileId, bufferPromise);
        }
        await bufferPromise;

        if (isMidi(fileData.fileType)) {
            let buffer = this.bufferMap[fileData.fileId];
            let gainNode = getAudioContext().createGain();
            buffer.reverberator.output.connect(gainNode);
            // gainNode.connect(getAudioContext().destination);
            player.gainNode = gainNode;
            this.adjustGainNode(player, getAudioContext());
        } else {
            // let gainNode = getAudioContext().createGain();
            // gainNode.connect(getAudioContext().destination);
            // player.gainNode = gainNode;
            // this.adjustGainNode(player, getAudioContext());
        }

        this.initSplits(player);
        player.splitSources = [];
        eventsWithDefault.onDurationChange(player);
        if (!player.mediaElement) { // for media elements we use the canplay method
            eventsWithDefault.onLoaded(player);
        }
        // console.log('subscribe called for: ', player);
        return id;
    };

    decodeRawBuffer = async chunk => {
        return new Promise((resolve, reject) => {
            getAudioContext().decodeAudioData(chunk.slice(0), decodedBuffer => {
                resolve(decodedBuffer);
            }, (err) => {
                reject(err);
            });
        });
    };

    decodeMidiBuffer = async rawBuffer => {
        let song = parseMidiBuffer(rawBuffer);

        let audioContext = getAudioContext();
        let player = new WebAudioFontPlayer();
        let reverberator = player.createReverberator(audioContext);
        // reverberator.output.connect(audioContext.destination);
        let input = reverberator.input;
        for (let i = 0; i < song.tracks.length; i++) {
            let nn = player.loader.findInstrument(song.tracks[i].program);
            let info = player.loader.instrumentInfo(nn);
            song.tracks[i].info = info;
            song.tracks[i].id = nn;
            player.loader.startLoad(audioContext, info.url, info.variable);
        }
        for (let i = 0; i < song.beats.length; i++) {
            let nn = player.loader.findDrum(song.beats[i].n);
            let info = player.loader.drumInfo(nn);
            song.beats[i].info = info;
            song.beats[i].id = nn;
            player.loader.startLoad(audioContext, info.url, info.variable);
        }
        return new Promise((resolve, reject) => {
            player.loader.waitLoad(() => {
                resolve({song, player, reverberator, input});
            });
        })
    };

    // prepareTrackForPlay = track => {
    //     let splitSources = [];
    //     track.splits.forEach((split, i) => {
    //         let source = getAudioContext().createBufferSource();
    //         source.buffer = this.bufferMap[track.fileId];
    //
    //         source.connect(track.gainNode);
    //         splitSources[i] = source;
    //     });
    //     this.adjustGainNode(track);
    //     track.splitSources = splitSources;
    // };
    //
    // onAudioProcess(track, source) {
    //     let endTime = track.loopEnd || source.loopEnd;
    //     let startTime = track.loopStart || source.loopStart;
    //     try {
    //         let time = (getAudioContext().currentTime - track.playStartedTime) || 0;
    //         if (time < 0) {
    //             return;
    //         }
    //         track.currentTime = time;
    //         // console.log('time: ' + time + 'currentTime: ' + track.currentTime);
    //         if (time >= endTime) {
    //             if (!track.loop) {
    //                 // we finished playing
    //                 this.stop(track.id);
    //                 track.events.onEnd(track.loopStart, track.loopEnd);
    //             } else {
    //                 // we need to continue playing
    //                 track.playStartedTime = track.playStartedTime + endTime; // === getAudioContext().currentTime at the at the beginning (let time = ...)
    //                 track.currentTime = startTime;
    //                 // console.log('continue play: time: ' + time + ', playstartTime:' + track.playStartedTime + ' endTime: ' + track.loopEnd);
    //                 track.events.onStopPause(track);
    //             }
    //         } else {
    //             // track.events.onTimeUpdate(track);
    //         }
    //     } catch (err) {
    //     }
    // }

    getCurrentTime = trackID => {
        let track = this.playerMap.get(trackID);
        if (!isEmpty(track)) {
            return track.currentTime || 0;
        }
    };

    getDuration = trackID => {
        let track = this.playerMap.get(trackID);
        if (!isEmpty(track)) {
            return track.duration || 0;
        }
        return 0;
    };

    adjustGainNode = (track, audioContext) => {
        let currentTime = audioContext.currentTime;
        if (track.gainNode) {
            track.gainNode.gain.setValueAtTime(track.volume * track.masterVolume * 4, currentTime);
            track.gainNode.gain.linearRampToValueAtTime(track.volume * track.masterVolume * 4, currentTime + 0.01);
        }

        // track.splits.forEach(split => {
        //     if (split.gainNode) {
        //         split.gainNode.gain.setValueAtTime(track.volume * 2, currentTime);
        //         split.gainNode.gain.linearRampToValueAtTime(track.volume * 2, currentTime + 0.01);
        //     }
        // });
    };

    unsubscribeFileId = (fileId) => {
        this.unsubscribeMap[fileId] = true;
        this.bufferMap[fileId] = null;
        this.rawBufferMap[fileId] = null;
        this.bufferPromiseMap.delete(fileId);
    };
    /**
     * Unsubscribe the player (remove it from audio store)
     * @param playerId   {String}    The audio store player id
     */
    unsubscribe = (playerId) => {
        // console.log('unsub: ' + playerId);
        this.stop([playerId]);
        let track = this.playerMap.get(playerId);
        this.playerMap.delete(playerId);
        if (!isEmpty(track)) {
            // let playBuffer = this.cloneBuffer(this.bufferMap.get(track.fileId));
            let areThereOtherPlayersOnSameFileId = false;
            this.playerMap.forEach(otherTrack => {
                if (otherTrack.fileId === track.fileId) {
                    areThereOtherPlayersOnSameFileId = true;
                }
            });
            if (!areThereOtherPlayersOnSameFileId && track.fileId) {
                // console.log('clear!!');
                this.bufferMap[track.fileId] = null;
                this.rawBufferMap[track.fileId] = null;
                this.bufferPromiseMap.delete(track.fileId);
            } else {
                console.log('found other tracks using the same fileId: ' + track.fileId);
            }
        }
    };

    /**
     * Perform the stop functionality
     * @param playerIds   {Array} The audio store item ids (String)
     */
    stop = (playerIds, suppresEvents = false) => {
        // console.log('calling stop for : ', playerIds)
        if (typeof playerIds !== 'object') {
            playerIds = [playerIds];
        }
        this.keepAlive = null;
        if (playerIds) {
            playerIds.forEach(playerId => {
                let track = this.playerMap.get(playerId);
                // console.log('calling stop: ' + track.currentTime);
                if (!isEmpty(track)) {
                    if (track.mediaElement) {
                        track.mediaElement.pause();
                        track.mediaElement.currentTime = 0;
                    } else if (isMidi(track.fileType)) {
                        let midiPlayer = this.bufferMap[track.fileId];
                        let player = midiPlayer.player;
                        player.cancelQueue(getAudioContext());
                    } else {
                        clearTimeout(this.stopPauseTimeout);
                        clearInterval(this.stopPauseInterval);
                        clearInterval(this.keepAliveInterval);
                    }
                    track.playing = false;
                    track.partialPlay = false;
                    track.scheduledPause = 0;
                    track.currentTime = 0;
                    track.playStartedTime = 0;
                    if (!suppresEvents) {
                        track.events.onStopPause(track);
                    }
                }
            });
        }

        try {
            let anyPlaying = false;
            this.playerMap.forEach(track => {
                anyPlaying = (anyPlaying || track.playing)
            });
            if (!anyPlaying) {
                this.scriptNode.disconnect();
                this.scriptNode.onaudioprocess = null
            }
        } catch (err) {
        }
    };

    setSlice = (playerId, start, end) => {
        let track = this.playerMap.get(playerId);
        if (!isEmpty(track)) {
            track.loopStart = start;// - track.startOffset;
            track.loopEnd = Math.min(end, track.paddingDuration);// - track.startOffset;
            // if (track.currentTime < track.loopStart || track.currentTime > track.loopEnd) {
            //     track.currentTime = track.loopStart;
            // }
        }
    };

    unsetSlice = (playerId) => {
        let track = this.playerMap.get(playerId);
        if (!isEmpty(track)) {
            track.loopStart = null;
            track.loopEnd = null;
        }
    };

    /**
     * Perform the play functionality
     * @param playerIds   {Array} The audio store item ids (String)
     * @param immediate if true the play will happen immediate without any delay, important for the recorder. otherwise the delay is minimal but
     */
    play = (playerIds, immediate = false, delay, exportAudio = false, doneCallback = null, suppressEvents = false, pauseAllCallback = null) => {
        // console.log(playerIds);
        if (typeof playerIds !== 'object') {
            playerIds = [playerIds];
        }
        let tracks = playerIds.map(playerId => {
            return this.playerMap.get(playerId);
        });
        const loopStart = tracks[0].loopStart || 0;
        let paddingDuration = tracks[0].loopEnd || tracks[0].paddingDuration;
        const trackCurrentTime = tracks[0].currentTime;
        let trackStartTime = Math.max(loopStart, trackCurrentTime);
        if (!isNumber(trackStartTime) || trackStartTime < 0) {
            trackStartTime = 0;
            customEvent(event.CustomEvent, {category: category.Studio, action: actions.Error, label: 'trackStartTime is : ' + trackStartTime})
        }

        clearInterval(this.keepAliveInterval);


        let audioContext = !exportAudio ? getAudioContext() : createOfflineAudioContext(2, paddingDuration * 44100, 44100);
        let sampleRate = audioContext.sampleRate;
        let currentTime = audioContext.currentTime;
        let loop = tracks[0].loop;
        let mediaElement = !!tracks[0].mediaElement;
        if (!mediaElement) {
            if (this.scriptNode) {
                this.scriptNode.onaudioprocess = null;
                this.scriptNode.disconnect();
                this.scriptNode = null;
            }
        }

        let scriptNode = audioContext.createScriptProcessor(bufferSize, 2, 2);
        if (!mediaElement) {
            this.scriptNode = scriptNode;
        }

        let fixedDelay = delay ? (delay - 2 * bufferSize / sampleRate) : 0;

        // console.log('@@@ play called: immediate: ', immediate, ' trackStartTime: ', trackStartTime, ', paddingDuration: ', paddingDuration, 'playerIds: ', playerIds);
        let songStart = currentTime + 0.1;
        for (let i = 0; i < tracks.length; i++) {
            let track = tracks[i];
            track.loop = loop;

            if (track.mediaElement && !isMidi(track.fileType)) {
                this.playMediaElement(track);
            } else {
                track.playStartedTime = currentTime;
                track.currentTime = trackStartTime;
                if (isMidi(track.fileType)) {
                    track.gainNode.connect(scriptNode);
                    this.playMidi(track, paddingDuration, trackStartTime, trackStartTime + 2 * bufferSize / sampleRate, songStart, audioContext);
                } else {
                    this.playWebAudio(track, paddingDuration, trackStartTime, audioContext, sampleRate);
                }
            }
            track.playing = true;
        }
        if (mediaElement) {
            return;
        }

        let num = trackStartTime * sampleRate;
        let ol = new Float32Array(bufferSize);
        let or = new Float32Array(bufferSize);
        let firstPlay = true;
        let process = (startFrom) => {
            for (let t = 0; t < tracks.length; t++) {
                let track = tracks[t];
                if (track.mediaElement || isMidi(track.fileType) || !track.playing) {
                    continue;
                }
                track.currentTime = num / sampleRate;
                for (let j = 0; j < track.splits.length; j++) {
                    let split = track.splits[j];
                    if (!split.splitWhen && split.splitWhen !== 0) {
                        continue;
                    }
                    const time = startFrom - split.splitWhen * sampleRate;
                    if ((time === startFrom || time + bufferSize > 0) && !split.done && !split.silence) {
                        if (track.playbackRate !== 1 || track.pitch !== 1) {
                            split.framesExtracted = split.filter.extract(split.samples, bufferSize);
                        } else {
                            split.framesExtracted = split.newSource.extract(split.samples, bufferSize, split.filter.sourcePosition);
                            split.filter.sourcePosition += bufferSize;
                        }

                        let upto = bufferSize;
                        if (split.counter + bufferSize >= split.splitDuration * sampleRate) {
                            // this is our last time...
                            let weAreLeftWith = Math.min(split.maxSamples, split.splitDuration * sampleRate) - split.counter;
                            if (weAreLeftWith > split.numSamples) {
                                split.framesExtracted = Math.min(split.framesExtracted, weAreLeftWith);
                                upto = split.framesExtracted;
                            } else {
                                split.numSamples = weAreLeftWith;
                                split.framesExtracted = 0;
                            }
                            split.done = true;
                        }
                        let numSamples = split.numSamples;
                        if (time < 0) {
                            numSamples = Math.floor(-time);
                        } else {
                            for (let i = 0; i < numSamples; i++) {
                                ol[i] += split.leftoversLeft[i] * track.volume * track.masterVolume * 4;
                                or[i] += split.leftoversRight[i] * track.volume * track.masterVolume * 4;
                                split.counter++;
                            }
                        }
                        if (split.framesExtracted > 0) {
                            for (let i = numSamples; i < upto; i++) {
                                ol[i] += split.samples[(i - numSamples) * 2] * track.volume * track.masterVolume * 4;
                                or[i] += split.samples[(i - numSamples) * 2 + 1] * track.volume * track.masterVolume * 4;
                                split.counter++;
                            }
                        }
                        if (split.framesExtracted < bufferSize && !split.done) {
                            numSamples = split.framesExtracted - bufferSize + numSamples; // 3684
                            if (numSamples < 0) {
                                split.done = true;
                            }
                        }
                        for (let i = 0; i < numSamples; i++) {
                            split.leftoversLeft[i] = split.samples[(split.framesExtracted - numSamples + i) * 2];
                            split.leftoversRight[i] = split.samples[(split.framesExtracted - numSamples + i) * 2 + 1];
                        }
                        split.numSamples = numSamples;
                    }
                    // console.log('split ', j, ' time:  ', time, ' currentTime (num): ', num * bufferSize / 44100, ', splitWhen: ', split.splitWhen, ' framesExtracted: ', split.framesExtracted, 'counter: ', split.counter);

                }
            }

        };
        let offset = 0;


        // let time = new Date();
        scriptNode.onaudioprocess = (e) => {
            // let currentTime = new Date();
            // const diff = (currentTime - time);
            // if (diff / 1000 > (bufferSize / sampleRate)*1.01) {
            //     console.log(":שרשרת טיימינג הלכה" + diff);
            //     time = currentTime;
            // }
            this.keepAlive = Date.now();
            if (firstPlay) {
                firstPlay = false;
                this.playStartTime = audioContext.currentTime + bufferSize / sampleRate;
                this.trackStartTime = trackStartTime;
                if (!suppressEvents) {
                    setTimeout(() => {
                        if (doneCallback) {
                            doneCallback();
                        }
                        clearTimeout(this.stopPauseTimeout);
                        clearInterval(this.stopPauseInterval);
                        this.stopPauseTimeout = setTimeout(() => {
                            tracks.map(track => {
                                track.events.onPlay(track)
                            });
                            this.stopPauseInterval = setInterval(() => {
                                tracks.map(track => {
                                    track.events.onPlay(track)
                                });
                            }, (paddingDuration - (tracks[0].loopStart || 0)) * 1000);
                        }, (paddingDuration - trackStartTime) * 1000);

                        tracks.map(track => track.events.onPlay(track));
                    }, bufferSize / sampleRate * 1000);
                }
            }

            ol.set(e.inputBuffer.getChannelData(0));
            or.set(e.inputBuffer.getChannelData(1));
            process(num);
            tracks.map(track => {
                if (isMidi(track.fileType)) {
                    this.playMidi(track, paddingDuration, (num + 2 * bufferSize) / sampleRate, (num + 3 * bufferSize) / sampleRate, songStart, audioContext);
                }
            });
            if ((num + bufferSize) > (offset + paddingDuration) * sampleRate) {
                trackStartTime = tracks[0].loopStart || 0;
                // num = 0;//Math.round(num + bufferSize - paddingDuration * 44100 + trackStartTime * 44100);
                let newOffset = offset + (paddingDuration - num / sampleRate);
                newOffset = Math.round(newOffset * sampleRate) / sampleRate;
                songStart += (paddingDuration - trackStartTime);

                tracks.map(track => {
                    if (track.mediaElement) {
                        return;
                    }
                    track.currentTime = trackStartTime;
                    if (isMidi(track.fileType)) {
                        this.playMidi(track, paddingDuration, trackStartTime, trackStartTime + bufferSize / sampleRate, songStart, audioContext);
                        this.playMidi(track, paddingDuration, trackStartTime + bufferSize / sampleRate, trackStartTime + 2 * bufferSize / sampleRate, songStart, audioContext);
                    } else {
                        this.playWebAudio(track, paddingDuration, trackStartTime, audioContext, sampleRate);
                        track.splits.map(split => {
                            let newSplitWhen = split.splitWhen + offset + paddingDuration - num / sampleRate;
                            split.splitWhen = Math.round(newSplitWhen * sampleRate) / sampleRate
                        });
                    }
                });
                offset = newOffset;
                num = trackStartTime * sampleRate;
                process(num);
                if (loop) {
                    if (pauseAllCallback) {
                        pauseAllCallback(true);
                    }
                } else {
                    this.stop(playerIds);
                }
                // firstPlay = true;
            }
            num += bufferSize;
            e.outputBuffer.getChannelData(0).set(ol);
            e.outputBuffer.getChannelData(1).set(or);
        };

        if (exportAudio) {
            scriptNode.connect(audioContext.destination);
            return audioContext.startRendering();
        } else {
            setTimeout(() => {
                scriptNode.connect(audioContext.destination);
            }, fixedDelay * 1000);

            const interval = bufferSize / sampleRate * 1000;
            this.keepAliveInterval = setInterval(() => {
                if (this.keepAlive && (Date.now() - this.keepAlive > 4 * interval)) {
                    clearInterval(this.keepAliveInterval);
                    console.log('keep alive not received within 4 intervals');
                    customEvent(event.CustomEvent, {category: category.Studio, action: actions.Error, label: 'keepalive missed 4 intervals'});
                    this.pause(playerIds);
                    if (pauseAllCallback) {
                        pauseAllCallback(false);
                    }
                }
            }, interval)
        }

        // if (loop) {
        //     clearTimeout(this.nextSchedule); // make sure there are no loops lurking in the dark
        //     this.nextSchedule = setTimeout(() => {
        //         this.stop(playerIds);//, true, iter + 1);
        //     }, (paddingDuration - trackStartTime) * 1000);
        //     // console.log('setting next play to: ' + ((paddingDuration - trackStartTime) * 1000 - 150), ' timeout: ' + this.nextSchedule);
        // }
    };

    playWebAudio(track, paddingDuration, trackStartTime, audioContext, sampleRate) {
        track.splitSources = [];

        let buffer = this.bufferMap[track.fileId];
        if (!buffer) {
            return;
        }
        for (let j = 0; j < track.splits.length; j++) {
            let split = track.splits[j];
            // let source = track.splitSources[j];
            let splitWhen = split.when - track.currentTime;
            let offset = split.trackOffset;
            offset = Math.round(offset * 1e10) / 1e10;

            splitWhen -= track.latencyOffset || 0;

            let duration = split.duration;
            if (splitWhen < 0) {
                offset -= splitWhen;
                duration = Math.max(0, duration + splitWhen);
            }
            duration = Math.min(duration, paddingDuration - trackStartTime);
            split.splitDuration = duration;
            let skipSplit = duration <= 0 || splitWhen >= paddingDuration; //Math.sign(splitWhen + duration) === -1;
            // splitWhen = Math.max(splitWhen, 0);


            if (buffer.getChannelData(0)[0] !== 0) {
                splitWhen += 0.03125;
            }
            // if (track.volume > 0) {
            //     console.log('\t@@@ start: when: ', splitWhen, ', offset: ', offset, ' duration: ', duration, ' skip: ', skipSplit, ' baseWhen: ', baseWhen, ' currentTime: ', currentTime);
            // }
            if (skipSplit) { // due to precision issues Math.sign is better than < 0
                continue;
            }

            split.newSource = {
                extract: (target, numFrames, position) => {
                    let l = buffer.getChannelData(0);
                    let r = l;
                    if (buffer.numberOfChannels === 2) {
                        r = buffer.getChannelData(1);
                    }
                    for (let i = 0; i < numFrames; i++) {
                        target[i * 2] = l[i + position] || 0;
                        target[i * 2 + 1] = r[i + position] || 0;
                    }
                    let x = Math.max(0, Math.min(numFrames, l.length - position));
                    return x;
                }
            };

            if (track.pitch !== 1 || track.playbackRate !== 1) {
                split.st = new SoundTouch();
                split.st.pitch = track.pitch;
                split.st.tempo = track.playbackRate;
                split.filter = new SimpleFilter(split.newSource, split.st);
            } else {
                split.filter = {};
            }

            split.samples = new Float32Array(bufferSize * 2);
            split.filter.sourcePosition = Math.round(offset * track.playbackRate * sampleRate);

            split.leftoversLeft = new Float32Array(bufferSize);
            split.leftoversRight = new Float32Array(bufferSize);
            split.numSamples = 0;
            split.maxSamples = buffer.getChannelData(0).length / track.playbackRate;
            split.splitWhen = Math.max(0, splitWhen + track.currentTime);
            split.done = false;
            split.pos = 0;
            split.framesExtracted = 0;
            split.counter = 0;

            // console.log('track: ', track.id, ' split: ', j, ' sched start:  ', baseWhen + splitWhen, ' pitch: ' + track.pitch, ' rate: ' + track.playbackRate);

            // track.splitSources.push(source);

            // split.gainNode.gain.setValueAtTime(0, baseWhen + splitWhen);
            // split.gainNode.gain.linearRampToValueAtTime(track.volume * 2, baseWhen + splitWhen + 0.01);
            // source.connect(split.scriptNode);
            // source.start(baseWhen + splitWhen, offset, duration);
            // track.scriptNode.connect(track.gainNode);
            // split.gainNode.gain.setValueAtTime(track.volume * 2, baseWhen + splitWhen + duration - 0.01);
            // split.gainNode.gain.linearRampToValueAtTime(0, baseWhen + splitWhen + duration);
        }
        // track.splitSources = splitSources;
        this.adjustGainNode(track, audioContext);
        // track.gainNode.gain.setValueAtTime(0, baseWhen + splitWhen);
        // track.gainNode.gain.linearRampToValueAtTime(track.volume * 2, baseWhen + splitWhen + 0.01);
        // track.gainNode.gain.setValueAtTime(track.volume * 2, baseWhen + splitWhen + duration - 0.01);
        // track.gainNode.gain.linearRampToValueAtTime(0, baseWhen + splitWhen + duration);
    }

    playMidi(track, paddingDuration, start, end, baseWhen, audioContext) {
        let midiPlayer = this.bufferMap[track.fileId];
        let song = midiPlayer.song;
        let reverberator = midiPlayer.reverberator;
        let input = reverberator.input;
        let player = midiPlayer.player;

        for (let j = 0; j < track.splits.length; j++) {
            let split = track.splits[j];
            let splitWhen = split.when - track.currentTime;
            let offset = split.trackOffset;
            offset = Math.round(offset * 1e10) / 1e10;
            let duration = split.duration;
            if (splitWhen < 0) {
                offset -= splitWhen;
                duration = Math.max(0, duration - offset);
            }
            duration = Math.min(duration, paddingDuration - track.currentTime);
            let skipSplit = duration <= 0 || splitWhen >= paddingDuration; //Math.sign(splitWhen + duration) === -1;
            // console.log('\t@@@ start: when: ', splitWhen, ', offset: ', offset, ' duration: ', duration, ' skip: ', skipSplit, ' baseWhen: ', baseWhen, ' trackTime: ' + track.currentTime + ' currentTime: ', audioContext.currentTime);
            if (skipSplit) { // due to precision issues Math.sign is better than < 0
                continue;
            }
            splitWhen = Math.max(0, splitWhen + track.currentTime);

            for (let t = 0; t < song.tracks.length; t++) {
                let subtrack = song.tracks[t];
                for (let i = 0; i < subtrack.notes.length; i++) {
                    const noteWhen = subtrack.notes[i].when / track.playbackRate;
                    if (noteWhen >= start && noteWhen < end && noteWhen >= offset && noteWhen < (offset + duration)) {
                        let when = baseWhen + splitWhen - offset + noteWhen;
                        let noteDuration = Math.min(3, subtrack.notes[i].duration);
                        let instr = subtrack.info.variable;
                        let v = subtrack.volume / 7;
                        player.queueWaveTable(audioContext, input, window[instr], when, subtrack.notes[i].pitch, noteDuration, v, subtrack.notes[i].slides);
                    }
                }
            }
            for (let b = 0; b < song.beats.length; b++) {
                let beat = song.beats[b];
                for (let i = 0; i < beat.notes.length; i++) {
                    const noteWhen = beat.notes[i].when / track.playbackRate;
                    if (noteWhen >= start && noteWhen < end && noteWhen >= offset && noteWhen < (offset + duration)) {
                        let when = baseWhen + splitWhen - offset + noteWhen;
                        let noteDuration = 1.5;
                        let instr = beat.info.variable;
                        let v = beat.volume / 2;
                        player.queueWaveTable(audioContext, input, window[instr], when, beat.n, noteDuration, v);
                    }
                }
            }

        }

    }

    playMediaElement(track) {
        track.mediaElement.play();
        track.currentTime = track.mediaElement.currentTime;
        track.playing = true;
        track.events.onPlay(track);
        track.mediaElement.ontimeupdate = () => {
            track.currentTime = track.mediaElement.currentTime;
            track.events.onTimeUpdate(Object.assign({}, track, {currentTime: track.currentTime}));
        };
        track.mediaElement.onended = () => {
            track.playing = false;
            track.currentTime = 0;
            track.mediaElement.currentTime = 0;
            track.events.onStopPause(track);
        }
    }

    /**
     * Perform a reset for the player
     * @param playerIds   {Array} The audio store item ids (String)
     */
    reset = (playerIds) => {
        this.stop(playerIds);
        this.play(playerIds);
    };

    /**
     * Perform a pausePlay for the player
     * @param playerIds   {Array} The audio store item ids (String)
     */
    pausePlay = (playerIds) => {
        this.pause(playerIds);
        this.play(playerIds);
    };

    /**
     * Set the current time to a specific time
     * @param playerId   {String} The audio store item id
     * @param time  {Number}    The time in seconds to set to
     */
    setCurrentTime = (playerId, time) => {
        let track = this.playerMap.get(playerId);
        if (!isEmpty(track)) {
            track.currentTime = time;
        }
    };

    /**
     * Perform the stop functionality
     * @param playerIds   {Array} The audio store item ids (String)
     */
    pause = (playerIds, suppressEvents = false) => {
        // console.log('click pause: ' + playerIds);
        if (typeof playerIds !== 'object') {
            playerIds = [playerIds];
        }
        let currentTime = getAudioContext().currentTime;
        this.keepAlive = null;
        playerIds.forEach(playerId => {
                let track = this.playerMap.get(playerId);
                if (!isEmpty(track)) {
                    if (track.playing) {
                        if (track.mediaElement) {
                            track.mediaElement.pause();
                            track.mediaElement.ontimeupdate = null;
                        } else if (isMidi(track.fileType)) {
                            let midiPlayer = this.bufferMap[track.fileId];
                            let player = midiPlayer.player;
                            player.cancelQueue(getAudioContext());
                            if (track.gainNode) {
                                track.gainNode.disconnect(this.scriptNode);
                            }
                        } else {
                            clearTimeout(this.stopPauseTimeout);
                            clearInterval(this.stopPauseInterval);
                            clearInterval(this.keepAliveInterval);
                        }
                    }
                    if (!track.mediaElement) {
                        if (track.playing) {
                            let relativeCurrentTime = currentTime - this.playStartTime + this.trackStartTime;
                            if (track.loopStart) {
                                track.currentTime = track.loopStart + (relativeCurrentTime - track.loopStart) % (track.loopEnd - track.loopStart);
                            }
                        }
                    }
                    track.playing = false;
                    if (!suppressEvents) {
                        track.events.onStopPause(track);
                    }
                }
            }
        );


        try {
            let anyPlaying = false;
            this.playerMap.forEach(track => {
                anyPlaying = (anyPlaying || track.playing)
            });
            if (!anyPlaying) {
                this.scriptNode.disconnect();
                this.scriptNode.onaudioprocess = null;
                this.scriptNode = null;
            }
        } catch (err) {
        }

        // console.log('pause called: timeout: ' + this.nextSchedule);
        // clearTimeout(this.nextSchedule);
    };

    /**
     * Modify the player volume
     * @param playerId   {String} The audio store item id
     * @param volume    {Number}    The requested volume number (0-1)
     */
    volume = (playerId, volume) => {
        let track = this.playerMap.get(playerId);
        if (!isEmpty(track)) {
            track.volume = volume;
            this.adjustGainNode(track, getAudioContext());
        }
    };

    masterVolume = (playerIds, masterVolume) => {
        playerIds.forEach(playerId => {
            let track = this.playerMap.get(playerId);
            track.masterVolume = masterVolume;
            this.adjustGainNode(track, getAudioContext());
        });
    };

    /**
     * Perform the seek functionality
     * @param playerIds   {Array} The audio store item id
     * @param progress  {Number}    The progress to seek to (percentage - 0-100)
     */
    seekTo = (playerIds, progress) => {
        if (!Array.isArray(playerIds)) {
            playerIds = [playerIds];
        }
        let isMediaElement = false;
        playerIds.forEach(playerId => {
            let track = this.playerMap.get(playerId);
            if (!isEmpty(track) && !isNaN(track.paddingDuration * progress)) {
                let seekToTime = progress * track.paddingDuration;
                track.currentTime = seekToTime;// - track.startOffset;
                track.playStartedTime = getAudioContext().currentTime - track.currentTime;
                // console.log('seek to called with progress: ' + progress + ', currentTime: ' + track.currentTime + ', playStarted: ' + track.playStartedTime);
                track.events.onTimeUpdate(Object.assign({}, track, {currentTime: Math.max(track.currentTime, 0)}));
                if (track.mediaElement) {
                    track.mediaElement.currentTime = track.currentTime;
                    isMediaElement = true;
                }
            }
        });
    };


// /**
//  * Set player to unloop mode
//  * @param playerId   {String} The audio store item id
//  * @param onEnd {Function}  Function to be called on track play end
//  */
// unLoop = (playerId, onEnd) => {
//     let track = this.playerMap.get(playerId);
//     if (!isEmpty(track)) {
//         track.loop = false;
//         track.events.onEnd = onEnd || (() => {
//         });
//         if (track.playing) {
//             this.pause(playerId);
//             this.play(playerId, track.currentTime, track.duration);
//         }
//     }
// };
//
// /**
//  * Set player to run in loop
//  * @param playerId   {String} The audio store item id
//  * @param start
//  * @param end
//  */
// loop = (playerId, start = null, end = null) => {
//     let track = this.playerMap.get(playerId);
//     if (!isEmpty(track)) {
//         track.loop = true;
//         track.events.onEnd = (() => {
//         });
//         if (track.playing) {
//             this.pause(playerId);
//             this.play(playerId, start, end);
//         }
//     }
// };

    /**
     * stops ALL players
     *
     */
    pauseAll = (suppressEvents = false) => {
        this.pause([...this.playerMap.keys()], suppressEvents);
    };

    stopAll = (suppressEvents = false) => {
        this.stop([...this.playerMap.keys()], suppressEvents);
        this.keepAlive = null;
    };


    setLatencyOffset = (playerIds, latencyOffset) => {
        if (!Array.isArray(playerIds)) {
            playerIds = [playerIds];
        }

        // let anyTrackPlaying = false;
        playerIds.forEach(playerId => {
            let track = this.playerMap.get(playerId);
            if (!isEmpty(track)) {
                console.log('track:', track.id, ', playerID:', playerId + ', latencyOffset: ', latencyOffset);
                track.latencyOffset = latencyOffset;
            }
        });
    }

    padTracks = (playerIds, duration) => {
        if (!Array.isArray(playerIds)) {
            playerIds = [playerIds];
        }

        // let anyTrackPlaying = false;
        playerIds.forEach(playerId => {
            let track = this.playerMap.get(playerId);
            // console.log('track: ' + track.id + ', playerID: ' + playerId + ', paddingduration: ' + duration + ', old duration: ' + track.duration);
            if (!isEmpty(track)) {
                track.paddingDuration = duration;
            }
            // anyTrackPlaying = anyTrackPlaying || track.playing;
        });

        // if (anyTrackPlaying) {
        //     let playerIds = [...this.playerMap.values()].filter(track => track.playing).map(track => track.id);
        //     this.pauseAll();
        //     this.play(playerIds, true);
        // }
    };

    changePlaybackRate = (playerIds, playbackRate, playbackRateFromLast, pitch) => {
        if (!Array.isArray(playerIds)) {
            playerIds = [playerIds];
        }

        let anyTrackPlaying = false;
        playerIds.forEach(playerId => {
            let track = this.playerMap.get(playerId);
            // console.log('track: ' + track.id + ', playerID: ' + playerId + ', paddingduration: ' + duration + ', old duration: ' + track.duration);
            if (!isEmpty(track)) {
                if (!track.ignoreBPM) {
                    track.playbackRate = playbackRate;
                    track.currentTime /= playbackRateFromLast;
                    track.duration /= playbackRateFromLast;
                    track.playbackRateFromLast = playbackRateFromLast;
                }
                if (!track.ignorePitch) {
                    track.pitch = 1 + (Math.pow(2, 1 / 12) - 1) * pitch;
                }
            }
            anyTrackPlaying = anyTrackPlaying || track.playing;
        });

        // if (anyTrackPlaying) {
        //     let playerIds = [...this.playerMap.values()].filter(track => track.playing).map(track => track.id);
        //     this.pauseAll();
        //     this.play(playerIds, true);
        // }
    };

    handlePaste = (splitIndex, target, track) => {
        let split = track.splits[splitIndex];
        if (split) {
            split.when = target.start;// paste target
            split.trackOffset = this.clipboardInfo.trackOffset + (this.clipboardInfo.target.start - this.clipboardInfo.absStart);
            split.duration = this.clipboardInfo.target.end - this.clipboardInfo.target.start;
            split.fullDuration = split.duration;
        } else {
            console.warn('paste failed to find split: ' + splitIndex);
        }
    };

    handleCopy = (splitIndex, target, track) => {
        let split = track.splits[splitIndex];
        this.clipboardInfo = {trackOffset: split.trackOffset, absStart: (split.offset + (split.start || split.leftTrim)), target};
    };

    handleStartOffset = (splitIndex, target, track) => {
        let split = track.splits[splitIndex];
        let delta = target.start - (split.offset || 0);
        split.when += delta;
        split.offset = target.start;
    };

    handleLeftTrim = (splitIndex, target, track) => {
        let split = track.splits[splitIndex];
        let delta = target.start - (split.leftTrim || 0);
        split.when += delta;
        split.trackOffset += delta;
        split.leftTrim = target.start;
        split.duration = (split.rightTrim || split.end) - split.leftTrim;
    };

    handleRightTrim = (splitIndex, target, track) => {
        let split = track.splits[splitIndex];
        split.rightTrim = target.start;
        if (split.rightTrim - (split.leftTrim || split.start) > 0) {
            split.duration = split.rightTrim - (split.leftTrim || split.start);
        }
    };

    roundTime = (time) => {
        return Math.round(time * 10000) / 10000;
    };


    handleSplit = (target, track, remove = false) => {
        let startBar = target.start;
        let endBar = target.end;
        let newSplits = [];
        let splitStart, splitEnd;
        track.splits.forEach(split => {
            const {start, end, leftTrim, rightTrim, offset, when, trackOffset, duration} = split;
            splitStart = (leftTrim || start) + offset;
            splitEnd = (rightTrim || end) + offset;
            if (endBar < splitStart || splitEnd <= startBar) {
                newSplits.push(split);
            } else {
                if (startBar > splitStart) {
                    // first split
                    newSplits.push({
                        start: start,
                        end: startBar - offset,
                        selectedBeats: [],
                        offset: offset,
                        leftTrim: leftTrim,
                        rightTrim: 0,
                        when: when,
                        trackOffset: trackOffset,
                        duration: (startBar - offset) - start
                    });
                }
                if (endBar < splitEnd) {
                    // last split
                    newSplits.push({
                        start: endBar - offset, end: end,
                        selectedBeats: [],
                        offset: offset,
                        leftTrim: 0,
                        rightTrim: rightTrim,
                        when: endBar,
                        trackOffset: trackOffset + (endBar - splitStart),
                        duration: end - (endBar - offset)
                    });
                }
            }
        });
        if (!remove) {
            // new split
            newSplits.push({start: startBar, end: endBar, selectedBeats: [], offset: 0, leftTrim: startBar, rightTrim: endBar});
        }
        newSplits.sort((a, b) => {
            return ((a.leftTrim || a.start) + a.offset) - ((b.leftTrim || b.start) + b.offset);
        });
        track.splits = newSplits;
        // console.log('audio splits: ' + JSON.stringify(track.splits));
    };

    applyAction = (track, action) => {
        // console.log('Action: ' + action.type);
        switch (action.type) {
            case TrackActionType.Paste:
                this.handlePaste(action.split, action.targetTime, track);
                break;
            case TrackActionType.StartOffset:
                this.handleStartOffset(action.split, action.targetTime, track);
                break;
            case TrackActionType.Copy:
                this.handleCopy(action.split, action.targetTime, track);
                break;
            // case TrackActionType.Padding:
            //     playBuffer = this.handlePadTrack(playBuffer, track, action.targetTime.start);
            //     break;
            case TrackActionType.LeftTrimOffset:
                this.handleLeftTrim(action.split, action.targetTime, track);
                break;
            case TrackActionType.RightTrimOffset:
                this.handleRightTrim(action.split, action.targetTime, track);
                break;
            case TrackActionType.Split:
                this.handleSplit(action.targetTime, track);
                break;
            case TrackActionType.SplitRemove:
                this.handleSplit(action.targetTime, track, true);
                break;
            default:
                break;
        }
    };


    /**
     * apply audio actions to the player's playbuffers
     * @param playerId
     * @param audioActions
     */
    applyActions = (playerId, audioActions, forcePlay = false) => {
        // console.log('apply actions: ' + playerId);
        const track = this.playerMap.get(playerId);
        if (!isEmpty(track)) {
            //track.duration = track.originalDuration || track.duration;
            this.initSplits(track);
            if (audioActions && audioActions.length > 0) {
                audioActions.forEach(action => {
                    this.applyAction(track, action);
                });
            }
            // track.splits.forEach((split, i) => {
            //     console.log('split: ', i, ' data: ', JSON.stringify(split))
            // });
            //  track.events.onDurationChange(track, true);
        }
    };

    applyActionsDebounce = this.applyActions;

    createAndSubscribeNewStem = async (newFileId, playerId, startTime, endTime, events = {}) => {
        const track = this.playerMap.get(playerId);
        if (!isEmpty(track)) {
            let fullBuffer = this.bufferMap[track.fileId];
            let totalCopyLength = (endTime - startTime) * fullBuffer.sampleRate;
            let newBuffer = getAudioContext().createBuffer(fullBuffer.numberOfChannels, totalCopyLength, fullBuffer.sampleRate);
            copyBuffer(newBuffer, fullBuffer, null, Math.floor(startTime * fullBuffer.sampleRate));

            return await this.subscribe({
                fileId: newFileId,
                buffer: newBuffer,
                isCopy: true
            }, events, track.loop, endTime - startTime);
        }
    };

    getBuffer = (playerId) => {
        const track = this.playerMap.get(playerId);
        if (!isEmpty(track)) {
            return this.bufferMap[track.fileId];
        }
        return null;
    }
}

const audioManager = new AudioManager();
export default audioManager;
